import React from 'react';
import { Card } from '@/components/ui/card';
import { MapPin, TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface ZipData {
  zip: string;
  leads: number;
  percentage: number;
  risk: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  trend: 'increasing' | 'stable' | 'decreasing';
  blocked: number;
  city: string;
  state: string;
}

export const GeographicConcentration: React.FC = () => {
  const zipData: ZipData[] = [
    { 
      zip: '80112', 
      leads: 432, 
      percentage: 32.1, 
      risk: 'CRITICAL', 
      trend: 'increasing',
      blocked: 389,
      city: 'Englewood',
      state: 'CO'
    },
    { 
      zip: '90210', 
      leads: 89, 
      percentage: 6.7, 
      risk: 'MEDIUM', 
      trend: 'stable',
      blocked: 23,
      city: 'Beverly Hills',
      state: 'CA'
    },
    { 
      zip: '10001', 
      leads: 67, 
      percentage: 5.1, 
      risk: 'LOW', 
      trend: 'decreasing',
      blocked: 8,
      city: 'New York',
      state: 'NY'
    },
    { 
      zip: '60601', 
      leads: 45, 
      percentage: 3.4, 
      risk: 'LOW', 
      trend: 'stable',
      blocked: 5,
      city: 'Chicago',
      state: 'IL'
    },
    { 
      zip: '33101', 
      leads: 78, 
      percentage: 5.8, 
      risk: 'HIGH', 
      trend: 'increasing',
      blocked: 56,
      city: 'Miami',
      state: 'FL'
    },
    { 
      zip: '94102', 
      leads: 34, 
      percentage: 2.5, 
      risk: 'LOW', 
      trend: 'decreasing',
      blocked: 3,
      city: 'San Francisco',
      state: 'CA'
    }
  ];

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'CRITICAL':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'HIGH':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'MEDIUM':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-green-100 text-green-800 border-green-200';
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'increasing':
        return <TrendingUp className="w-4 h-4 text-red-500" />;
      case 'decreasing':
        return <TrendingDown className="w-4 h-4 text-green-500" />;
      default:
        return <Minus className="w-4 h-4 text-gray-500" />;
    }
  };

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'increasing':
        return 'text-red-600';
      case 'decreasing':
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };

  const totalLeads = zipData.reduce((sum, zip) => sum + zip.leads, 0);
  const totalBlocked = zipData.reduce((sum, zip) => sum + zip.blocked, 0);
  const criticalZips = zipData.filter(zip => zip.risk === 'CRITICAL' || zip.risk === 'HIGH').length;

  return (
    <Card className="p-6">
      <div className="flex items-center gap-3 mb-6">
        <MapPin className="w-6 h-6 text-blue-500" />
        <div>
          <h3 className="text-lg font-semibold text-foreground">Geographic Concentration</h3>
          <p className="text-sm text-muted-foreground">Lead distribution and risk analysis by location</p>
        </div>
      </div>

      {/* High-risk Alert */}
      {criticalZips > 0 && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2 text-red-800">
            <MapPin className="w-4 h-4" />
            <span className="font-medium text-sm">
              {criticalZips} high-risk location{criticalZips > 1 ? 's' : ''} detected
            </span>
          </div>
        </div>
      )}

      {/* Zip Code Table */}
      <div className="overflow-hidden border rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left py-3 px-4 font-medium text-foreground">Location</th>
              <th className="text-left py-3 px-4 font-medium text-foreground">Leads</th>
              <th className="text-left py-3 px-4 font-medium text-foreground">% of Total</th>
              <th className="text-left py-3 px-4 font-medium text-foreground">Blocked</th>
              <th className="text-left py-3 px-4 font-medium text-foreground">Risk Level</th>
              <th className="text-left py-3 px-4 font-medium text-foreground">Trend</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {zipData.map((zip, index) => (
              <tr key={index} className={`hover:bg-gray-50 ${
                zip.risk === 'CRITICAL' ? 'bg-red-50/30' : 
                zip.risk === 'HIGH' ? 'bg-orange-50/30' : ''
              }`}>
                <td className="py-3 px-4">
                  <div>
                    <div className="font-mono font-medium text-foreground">{zip.zip}</div>
                    <div className="text-xs text-muted-foreground">{zip.city}, {zip.state}</div>
                  </div>
                </td>
                <td className="py-3 px-4">
                  <div className="font-medium text-foreground">{zip.leads}</div>
                </td>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <div className="font-medium text-foreground">{zip.percentage}%</div>
                    <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${
                          zip.percentage > 20 ? 'bg-red-500' :
                          zip.percentage > 10 ? 'bg-orange-500' :
                          zip.percentage > 5 ? 'bg-yellow-500' : 'bg-green-500'
                        }`}
                        style={{ width: `${Math.min(zip.percentage * 3, 100)}%` }}
                      />
                    </div>
                  </div>
                </td>
                <td className="py-3 px-4">
                  <div>
                    <div className="font-medium text-red-600">{zip.blocked}</div>
                    <div className="text-xs text-muted-foreground">
                      {((zip.blocked / zip.leads) * 100).toFixed(1)}%
                    </div>
                  </div>
                </td>
                <td className="py-3 px-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getRiskColor(zip.risk)}`}>
                    {zip.risk}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    {getTrendIcon(zip.trend)}
                    <span className={`text-xs capitalize font-medium ${getTrendColor(zip.trend)}`}>
                      {zip.trend}
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-4 border-t">
        <div className="text-center">
          <div className="text-2xl font-bold text-foreground">{zipData.length}</div>
          <div className="text-xs text-muted-foreground">Monitored Locations</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-red-600">{criticalZips}</div>
          <div className="text-xs text-muted-foreground">High Risk Areas</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-orange-600">{totalBlocked}</div>
          <div className="text-xs text-muted-foreground">Total Blocked</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600">
            {zipData[0]?.percentage.toFixed(1)}%
          </div>
          <div className="text-xs text-muted-foreground">Highest Concentration</div>
        </div>
      </div>
    </Card>
  );
};